import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { api } from '@/lib/api';

/**
 * What state the outgoing stream is in, visible from every page.
 *
 * These three settings change what leaves for the ad platforms, and all three
 * were only visible on the page where you set them - so shadow mode could be on
 * for a week, or health redaction could be silently off, and the only way to
 * know was to go and look. A setting that alters every event in the product
 * should not be discoverable only from its own settings screen.
 *
 * Sits beside the search box because that bar is on screen everywhere, and the
 * space was empty.
 */

const MODES = {
  health: { label: 'Health mode', Icon: ShieldCheck,
    cls: 'border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300',
    title: 'URLs grouped, product names and search terms removed before sending' },
  pii: { label: 'PII stripped', Icon: ShieldCheck,
    cls: 'card-info text-sky-700 dark:text-sky-300',
    title: 'Emails, phone numbers and long ids removed from URLs and text' },
};

/**
 * The stream's state as rows for the Alerts bell. Sending off is a warning
 * (nothing leaves); a redaction mode is status, listed but not counted.
 */
export function useStreamAlerts() {
  const { data } = useQuery({
    queryKey: ['stream', 'chip'],
    queryFn: async () => (await api.get('/api/event-stream/state')).data,
    staleTime: 120_000,
    refetchInterval: 300_000,
    retry: false,
  });
  if (!data) return [];
  const out = [];
  if (data.paused || data.shadow) {
    out.push({ id: 'sending-off', tone: 'warn', text: 'Sending off: events are logged, nothing leaves for any ad platform',
      to: '/tracking/stream', linkLabel: 'Open Event stream' });
  }
  const m = MODES[data.mode];
  if (m) out.push({ id: `mode-${data.mode}`, tone: 'info', text: `${m.label}: ${m.title.charAt(0).toLowerCase()}${m.title.slice(1)}`, to: '/tracking/stream', linkLabel: 'Open Event stream' });
  return out;
}

export default useStreamAlerts;
